import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD, fmtDate, fmtCarat, fmtSize, SHAPES, buildParcelCode, computeSize } from "@/lib/format";
import { Plus, Package, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { FileUpload } from "@/components/FileUpload";
import { TableSummaryRow } from "@/components/TableSummaryRow";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTIONS = [
    { value: "available", label: "Available" },
    { value: "in_production", label: "In production" },
    { value: "set_in_jewelry", label: "Set in jewelry" },
    { value: "sold", label: "Sold" },
];
const DEFAULT_STATUSES = ["available", "in_production", "set_in_jewelry"]; // sold hidden by default
const PAYMENT_OPTIONS = [
    { value: "paid", label: "Fully paid" },
    { value: "partial", label: "Partially paid" },
    { value: "unpaid", label: "Unpaid" },
];
const ROOT_OPTIONS = [
    { value: "root", label: "Root parcels" },
    { value: "sub", label: "Sub-parcels" },
];
const FILTERS_STORAGE_KEY = "parcels-filters-v1";

const emptyForm = () => ({
    name: "", purchase_date: new Date().toISOString().slice(0, 10),
    shape: "round", total_carat: "", clarity: "VS", color: "D-F",
    number_of_stones: "", size: "", size_override: false, mm: "",
    price_per_carat: "", list_price_per_carat: "", bashari: "", ido: "",
    supplier: "",
    location: "", notes: "", photos: [], invoice: null,
});

// URL <-> filter state helpers (comma-joined lists, blank omitted).
const parseList = (raw) => (raw ? raw.split(",").filter(Boolean) : []);
const filtersFromParams = (params) => {
    const has = (k) => params.has(k);
    return {
        statuses: has("status") ? parseList(params.get("status")) : DEFAULT_STATUSES,
        payments: has("payment") ? parseList(params.get("payment")) : [],
        carats: parseList(params.get("carats")).map(Number).filter(n => !isNaN(n)),
        caratMin: params.get("carat_min") || "",
        caratMax: params.get("carat_max") || "",
        sizes: parseList(params.get("size")).map(Number).filter(n => !isNaN(n)),
        roots: has("root") ? parseList(params.get("root")) : [],
    };
};
const paramsFromFilters = (f) => {
    const p = new URLSearchParams();
    // Only serialize non-default facets so the URL stays clean.
    const statusesDefault = f.statuses.length === DEFAULT_STATUSES.length && DEFAULT_STATUSES.every(s => f.statuses.includes(s));
    if (!statusesDefault) p.set("status", f.statuses.join(","));
    if (f.payments.length) p.set("payment", f.payments.join(","));
    if (f.carats.length) p.set("carats", f.carats.join(","));
    if (f.caratMin !== "") p.set("carat_min", String(f.caratMin));
    if (f.caratMax !== "") p.set("carat_max", String(f.caratMax));
    if (f.sizes.length) p.set("size", f.sizes.join(","));
    if (f.roots.length) p.set("root", f.roots.join(","));
    return p;
};

export default function Parcels() {
    const { canWrite } = useAuth();
    const [parcels, setParcels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [suppliers, setSuppliers] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();

    // Hydrate from localStorage on first mount if URL has no filter params — so filters persist
    // across page navigations. URL always wins when present, keeping shareable links functional.
    useEffect(() => {
        const hasAnyParam = ["status", "payment", "carats", "carat_min", "carat_max", "size", "root"].some(k => searchParams.has(k));
        if (hasAnyParam) return;
        try {
            const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
            if (!saved) return;
            const parsed = new URLSearchParams(saved);
            if ([...parsed.keys()].length > 0) setSearchParams(parsed, { replace: true });
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

    // Persist current filter state whenever the URL changes.
    useEffect(() => {
        try { localStorage.setItem(FILTERS_STORAGE_KEY, searchParams.toString()); } catch { /* ignore quota */ }
    }, [searchParams]);

    const setFilters = (next) => setSearchParams(paramsFromFilters(next), { replace: true });
    const patchFilters = (patch) => setFilters({ ...filters, ...patch });
    const clearAll = () => setSearchParams(new URLSearchParams(), { replace: true });

    useEffect(() => {
        api.get("/parcels/suppliers").then(({ data }) => setSuppliers(data?.suppliers || [])).catch(() => { });
    }, [parcels]);

    async function load() {
        setLoading(true);
        try {
            // Fetch ALL parcels once (including sold); filtering happens client-side.
            const { data } = await api.get("/parcels", { params: { include_sold: true } });
            setParcels(data);
        } catch { toast.error("Failed to load parcels"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    // Distinct facet values from the dataset (for popover checklists).
    const distinctCarats = useMemo(() => {
        const s = new Set();
        for (const p of parcels) if (p.total_carat != null) s.add(Number(p.total_carat));
        return [...s].sort((a, b) => a - b);
    }, [parcels]);
    const distinctSizes = useMemo(() => {
        const s = new Set();
        for (const p of parcels) if (p.size != null && p.size !== "") s.add(Number(p.size));
        return [...s].filter(n => !isNaN(n)).sort((a, b) => a - b);
    }, [parcels]);

    const matches = (p) => {
        // Status
        if (filters.statuses.length && !filters.statuses.includes(p.status)) return false;
        // Payment
        if (filters.payments.length) {
            const s = p.payments_summary?.status;
            if (!s || !filters.payments.includes(s)) return false;
        }
        // Root/sub
        if (filters.roots.length) {
            const kind = p.parent_id ? "sub" : "root";
            if (!filters.roots.includes(kind)) return false;
        }
        // Size (multi)
        if (filters.sizes.length) {
            const psize = p.size == null ? null : Number(p.size);
            if (psize == null || !filters.sizes.some(v => Math.abs(v - psize) < 1e-6)) return false;
        }
        // Carat (checked-values UNION [min,max])
        const caratActive = filters.carats.length > 0 || filters.caratMin !== "" || filters.caratMax !== "";
        if (caratActive) {
            const c = Number(p.total_carat);
            const inChecked = filters.carats.some(v => Math.abs(v - c) < 1e-6);
            const minOk = filters.caratMin === "" || c >= Number(filters.caratMin);
            const maxOk = filters.caratMax === "" || c <= Number(filters.caratMax);
            const rangeActive = filters.caratMin !== "" || filters.caratMax !== "";
            const inRange = rangeActive && minOk && maxOk;
            if (!(inChecked || inRange)) return false;
        }
        return true;
    };

    // Sort parcels hierarchically (DFS from every root) + apply filters. Compute _depth per row.
    const sortedParcels = useMemo(() => {
        const byId = new Map(parcels.map(p => [p.id, p]));
        const childrenOf = new Map();
        for (const p of parcels) {
            const parentId = p.parent_id || "__root__";
            if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
            childrenOf.get(parentId).push(p);
        }
        for (const arr of childrenOf.values()) {
            arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "") || a.code.localeCompare(b.code));
        }
        const out = [];
        const visit = (nodeId, depth) => {
            for (const child of childrenOf.get(nodeId) || []) {
                if (matches(child)) out.push({ ...child, _depth: depth });
                visit(child.id, depth + 1);
            }
        };
        const roots = parcels.filter(p => !p.parent_id || !byId.has(p.parent_id));
        roots.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "") || a.code.localeCompare(b.code));
        for (const r of roots) {
            if (matches(r)) out.push({ ...r, _depth: 0 });
            visit(r.id, 1);
        }
        return out;
    }, [parcels, filters]); // eslint-disable-line react-hooks/exhaustive-deps

    // Human-readable summary of what's currently applied.
    const humanSummary = useMemo(() => {
        const bits = [];
        if (filters.roots.length === 1) bits.push(filters.roots[0] === "root" ? "root parcels only" : "sub-parcels only");
        if (filters.statuses.length && filters.statuses.length < STATUS_OPTIONS.length) {
            bits.push("status: " + filters.statuses.map(s => STATUS_OPTIONS.find(o => o.value === s)?.label).filter(Boolean).join(" or "));
        }
        if (filters.payments.length) {
            bits.push(filters.payments.map(v => PAYMENT_OPTIONS.find(o => o.value === v)?.label).filter(Boolean).join(" / ").toLowerCase());
        }
        if (filters.sizes.length) bits.push("size: " + filters.sizes.map(v => `${v.toFixed(2)} ct`).join(", "));
        const caratChecks = filters.carats.length ? filters.carats.map(v => `${v.toFixed(2)} ct`).join(", ") : "";
        const rangeStr = (filters.caratMin !== "" || filters.caratMax !== "")
            ? `${filters.caratMin || "0"}–${filters.caratMax || "∞"} ct`
            : "";
        if (caratChecks && rangeStr) bits.push(`carat: ${caratChecks} or ${rangeStr}`);
        else if (caratChecks) bits.push(`carat: ${caratChecks}`);
        else if (rangeStr) bits.push(`carat ${rangeStr}`);
        return bits.length ? `Showing ${bits.join(", ")}.` : "Showing everything.";
    }, [filters]);

    const anyFilterActive = filters.roots.length > 0
        || filters.payments.length > 0
        || filters.sizes.length > 0
        || filters.carats.length > 0
        || filters.caratMin !== ""
        || filters.caratMax !== ""
        || !(filters.statuses.length === DEFAULT_STATUSES.length && DEFAULT_STATUSES.every(s => filters.statuses.includes(s)));

    const toggleIn = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

    const totalCost = useMemo(() => {
        const c = Number(form.total_carat || 0);
        const p = Number(form.price_per_carat || 0);
        return +(c * p).toFixed(2);
    }, [form.total_carat, form.price_per_carat]);

    // Auto size (unless user overrode)
    const autoSize = computeSize(form.total_carat, form.number_of_stones);
    const displayedSize = form.size_override ? form.size : (autoSize ?? "");

    const contribSum = Number(form.bashari || 0) + Number(form.ido || 0);
    const unpaid = +(totalCost - contribSum).toFixed(2);
    const overpaid = contribSum > totalCost + 0.01;

    const previewCode = buildParcelCode({
        shape: form.shape, clarity: form.clarity, color: form.color,
    });

    const submit = async (e) => {
        e.preventDefault();
        if (overpaid) { toast.error("Contributions cannot exceed total cost"); return; }
        setSaving(true);
        try {
            const stones = form.number_of_stones === "" ? null : Number(form.number_of_stones);
            const sizeValue = form.size_override && form.size !== ""
                ? Number(form.size)
                : (stones ? computeSize(form.total_carat, stones) : null);
            await api.post("/parcels", {
                name: form.name || null,
                purchase_date: form.purchase_date,
                shape: form.shape,
                total_carat: Number(form.total_carat),
                clarity: form.clarity,
                color: form.color,
                number_of_stones: stones,
                size: sizeValue,
                mm: form.mm || null,
                price_per_carat: Number(form.price_per_carat),
                list_price_per_carat: form.list_price_per_carat === "" ? null : Number(form.list_price_per_carat),
                contributions: { bashari: Number(form.bashari || 0), ido: Number(form.ido || 0) },
                supplier: form.supplier || null,
                location: form.location || null,
                notes: form.notes || null,
                photos: form.photos || [],
                invoice: form.invoice || null,
            });
            toast.success(`Parcel created: ${previewCode}`);
            setOpen(false); setForm(emptyForm()); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this parcel?")) return;
        try { await api.delete(`/parcels/${id}`); toast.success("Deleted"); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="space-y-6 animate-fade-up" data-testid="parcels-page">
            <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Inventory</div>
                    <h1 className="font-display text-4xl sm:text-5xl leading-none tracking-tight">Parcels</h1>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                    {canWrite && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-parcel-button" className="rounded-none bg-stone-900 text-stone-50 hover:bg-stone-800">
                                <Plus className="h-4 w-4 mr-2" /> New parcel
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#faf7ef] border-stone-200 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-parcel-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">Record a parcel purchase</DialogTitle>
                                <DialogDescription className="text-stone-500 text-xs uppercase tracking-widest">Master lot · raw stones</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Name (optional)"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-name-input" /></Field>
                                    <Field label="Purchase date"><Input required type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-date-input" /></Field>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Shape">
                                        <Select value={form.shape} onValueChange={v => setForm({ ...form, shape: v })}>
                                            <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="parcel-shape-select"><SelectValue /></SelectTrigger>
                                            <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none max-h-64">
                                                {SHAPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label} ({s.code})</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Number of stones (optional)"><Input type="number" min="1" step="1" value={form.number_of_stones} onChange={e => setForm({ ...form, number_of_stones: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-stones-input" placeholder="leave empty if unknown" /></Field>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Clarity"><Input required value={form.clarity} onChange={e => setForm({ ...form, clarity: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-clarity-input" placeholder="VS, VVS, SI, VS-VVS…" /></Field>
                                    <Field label="Color"><Input required value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-color-input" placeholder="D, E, F, D-F, G-H…" /></Field>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Total carat"><Input required type="number" step="0.01" min="0" value={form.total_carat} onChange={e => setForm({ ...form, total_carat: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-carat-input" /></Field>
                                    <Field label="Price per carat (USD)"><Input required type="number" step="0.01" min="0" value={form.price_per_carat} onChange={e => setForm({ ...form, price_per_carat: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-ppc-input" /></Field>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="List price / carat (optional)">
                                        <Input type="number" step="0.01" min="0" value={form.list_price_per_carat} onChange={e => setForm({ ...form, list_price_per_carat: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-listppc-input" placeholder="recommended selling price / ct" />
                                    </Field>
                                    <div className="flex items-end">
                                        {form.list_price_per_carat && form.total_carat ? (
                                            <div className="w-full border border-emerald-500/40 bg-emerald-50 p-2">
                                                <div className="text-[10px] uppercase tracking-widest text-stone-500">Suggested total</div>
                                                <div className="num text-emerald-700 text-sm" data-testid="parcel-list-total-preview">{fmtUSD(Number(form.total_carat) * Number(form.list_price_per_carat))}</div>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label={
                                        <span className="flex items-center gap-2">
                                            Size (ct / stone)
                                            {form.size_override && (
                                                <button type="button" onClick={() => setForm({ ...form, size_override: false, size: "" })} className="text-[9px] uppercase tracking-widest text-stone-500 hover:text-emerald-700" data-testid="parcel-size-reset">
                                                    reset to auto
                                                </button>
                                            )}
                                        </span>
                                    }>
                                        <Input type="number" step="0.0001" min="0" value={displayedSize} placeholder={autoSize !== null ? "auto" : "—"} onChange={e => setForm({ ...form, size: e.target.value, size_override: true })} className="rounded-none bg-white border-stone-200" data-testid="parcel-size-input" />
                                    </Field>
                                    <Field label="MM (measurements)"><Input value={form.mm} onChange={e => setForm({ ...form, mm: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-mm-input" placeholder="3mm, 2x3, 4.5x4.5…" /></Field>
                                </div>

                                <div className="border border-stone-200 bg-white p-3 flex items-center justify-between" data-testid="parcel-code-preview-block">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-stone-500">Auto ID</div>
                                        <div className="font-mono text-emerald-700 text-lg" data-testid="parcel-code-preview">{previewCode}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-widest text-stone-500">Total cost</div>
                                        <div className="num text-2xl text-stone-900 font-light" data-testid="parcel-total-cost-preview">{fmtUSD(totalCost)}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Bashari initial payment (optional)"><Input type="number" step="0.01" min="0" value={form.bashari} onChange={e => setForm({ ...form, bashari: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-bashari-input" placeholder="0 if unpaid" /></Field>
                                    <Field label="Ido initial payment (optional)"><Input type="number" step="0.01" min="0" value={form.ido} onChange={e => setForm({ ...form, ido: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-ido-input" placeholder="0 if unpaid" /></Field>
                                </div>
                                <div className={`text-xs uppercase tracking-widest ${overpaid ? "text-rose-600" : (unpaid > 0.01 ? "text-amber-700" : "text-emerald-700")}`} data-testid="parcel-balance-hint">
                                    {overpaid ? `Overpaid by ${fmtUSD(-unpaid)}` : (unpaid > 0.01 ? `Unpaid balance ${fmtUSD(unpaid)} — record additional payments in the Payments tab later` : (totalCost > 0 ? "Fully funded ✓" : "Enter carat and price/carat"))}
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Supplier (optional)">
                                        <Input list="parcel-supplier-options" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-supplier-input" placeholder="e.g. ABC Diamonds Co." />
                                        <datalist id="parcel-supplier-options">
                                            {suppliers.map(s => <option key={s} value={s} />)}
                                        </datalist>
                                    </Field>
                                    <Field label="Location"><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-location-input" placeholder="e.g. Vault A" /></Field>
                                </div>
                                <Field label="Notes"><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="parcel-notes-input" /></Field>
                                <Field label="Photos (optional)">
                                    <FileUpload
                                        value={form.photos}
                                        onChange={photos => setForm({ ...form, photos })}
                                        accept="image/*"
                                        label="Upload photos"
                                        testid="parcel-photos"
                                    />
                                </Field>
                                <Field label="Purchase invoice (optional)">
                                    <FileUpload
                                        value={form.invoice}
                                        onChange={invoice => setForm({ ...form, invoice })}
                                        accept="image/*,application/pdf"
                                        label="Upload invoice (PDF or image)"
                                        testid="parcel-invoice"
                                        single
                                    />
                                </Field>
                                <DialogFooter>
                                    <Button type="submit" disabled={saving || overpaid} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-parcel-button">
                                        {saving ? "Saving…" : "Create parcel"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                    )}
                </div>
            </header>

            {/* Filter chip bar */}
            <div className="border border-stone-200 bg-white/50 p-3" data-testid="parcels-filter-bar">
                <div className="flex items-center gap-2 flex-wrap">
                    <FilterChip
                        label="Status"
                        count={(() => {
                            const isDefault = filters.statuses.length === DEFAULT_STATUSES.length && DEFAULT_STATUSES.every(s => filters.statuses.includes(s));
                            return isDefault ? 0 : filters.statuses.length;
                        })()}
                        testid="filter-chip-status"
                    >
                        <ChipList>
                            {STATUS_OPTIONS.map(o => (
                                <ChipOption key={o.value} checked={filters.statuses.includes(o.value)}
                                    onChange={() => patchFilters({ statuses: toggleIn(filters.statuses, o.value) })}
                                    testid={`filter-status-${o.value}`}>{o.label}</ChipOption>
                            ))}
                        </ChipList>
                    </FilterChip>

                    <FilterChip label="Payment" count={filters.payments.length} testid="filter-chip-payment">
                        <ChipList>
                            {PAYMENT_OPTIONS.map(o => (
                                <ChipOption key={o.value} checked={filters.payments.includes(o.value)}
                                    onChange={() => patchFilters({ payments: toggleIn(filters.payments, o.value) })}
                                    testid={`filter-payment-${o.value}`}>{o.label}</ChipOption>
                            ))}
                        </ChipList>
                    </FilterChip>

                    <FilterChip
                        label="Carat"
                        count={filters.carats.length + (filters.caratMin !== "" || filters.caratMax !== "" ? 1 : 0)}
                        testid="filter-chip-carat"
                    >
                        <div className="min-w-[220px]">
                            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Values in this list</div>
                            <ChipList scrollable>
                                {distinctCarats.length === 0 && <div className="text-xs text-stone-400 italic px-1 py-2">no carats yet</div>}
                                {distinctCarats.map(v => (
                                    <ChipOption key={v} checked={filters.carats.some(x => Math.abs(x - v) < 1e-6)}
                                        onChange={() => {
                                            const already = filters.carats.some(x => Math.abs(x - v) < 1e-6);
                                            const next = already ? filters.carats.filter(x => Math.abs(x - v) >= 1e-6) : [...filters.carats, v];
                                            patchFilters({ carats: next });
                                        }}
                                        testid={`filter-carat-${v.toFixed(2)}`}>{v.toFixed(2)} ct</ChipOption>
                                ))}
                            </ChipList>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-3 mb-1">Or by range</div>
                            <div className="grid grid-cols-2 gap-2">
                                <Input type="number" step="0.01" min="0" placeholder="min" value={filters.caratMin}
                                    onChange={e => patchFilters({ caratMin: e.target.value })}
                                    className="rounded-none bg-white border-stone-200 h-8 text-xs" data-testid="filter-carat-min" />
                                <Input type="number" step="0.01" min="0" placeholder="max" value={filters.caratMax}
                                    onChange={e => patchFilters({ caratMax: e.target.value })}
                                    className="rounded-none bg-white border-stone-200 h-8 text-xs" data-testid="filter-carat-max" />
                            </div>
                        </div>
                    </FilterChip>

                    <FilterChip label="Size" count={filters.sizes.length} testid="filter-chip-size">
                        <ChipList scrollable>
                            {distinctSizes.length === 0 && <div className="text-xs text-stone-400 italic px-1 py-2">no sizes recorded</div>}
                            {distinctSizes.map(v => (
                                <ChipOption key={v} checked={filters.sizes.some(x => Math.abs(x - v) < 1e-6)}
                                    onChange={() => {
                                        const already = filters.sizes.some(x => Math.abs(x - v) < 1e-6);
                                        const next = already ? filters.sizes.filter(x => Math.abs(x - v) >= 1e-6) : [...filters.sizes, v];
                                        patchFilters({ sizes: next });
                                    }}
                                    testid={`filter-size-${v.toFixed(2)}`}>{v.toFixed(2)} ct</ChipOption>
                            ))}
                        </ChipList>
                    </FilterChip>

                    <FilterChip label="Root / sub" count={filters.roots.length} testid="filter-chip-root">
                        <ChipList>
                            {ROOT_OPTIONS.map(o => (
                                <ChipOption key={o.value} checked={filters.roots.includes(o.value)}
                                    onChange={() => patchFilters({ roots: toggleIn(filters.roots, o.value) })}
                                    testid={`filter-root-${o.value}`}>{o.label}</ChipOption>
                            ))}
                        </ChipList>
                    </FilterChip>

                    <div className="ml-auto">
                        {anyFilterActive && (
                            <button onClick={clearAll} className="text-[11px] uppercase tracking-widest text-stone-500 hover:text-rose-600 transition-colors inline-flex items-center gap-1" data-testid="filter-clear-all">
                                <X className="h-3 w-3" /> Clear all
                            </button>
                        )}
                    </div>
                </div>
                <div className="text-[11px] text-stone-500 mt-2" data-testid="parcels-filter-summary">{humanSummary}</div>
            </div>

            {loading ? (
                <div className="text-stone-500 text-sm">Loading…</div>
            ) : parcels.length === 0 ? (
                <div className="border border-stone-200 p-10 text-center bg-[#f5efe0]">
                    <Package className="mx-auto h-8 w-8 text-stone-500" strokeWidth={1.2} />
                    <h3 className="font-display text-2xl mt-4">No parcels yet</h3>
                    <p className="text-sm text-stone-500 mt-1">Start by recording your first master lot purchase.</p>
                </div>
            ) : sortedParcels.length === 0 ? (
                <div className="border border-stone-200 p-10 text-center bg-[#f5efe0]" data-testid="parcels-no-match">
                    <Package className="mx-auto h-8 w-8 text-stone-500" strokeWidth={1.2} />
                    <h3 className="font-display text-2xl mt-4">No parcels match these filters</h3>
                    <button onClick={clearAll} className="text-sm text-emerald-700 hover:text-emerald-800 mt-2 underline" data-testid="filter-clear-all-empty">Clear all filters</button>
                </div>
            ) : (
                <div className="border border-stone-200 overflow-x-auto" data-testid="parcels-table">
                    <table className="w-full text-sm min-w-[1000px]">
                        <thead>
                            <tr className="text-left border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500">
                                <th className="py-3 px-4">Code</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4">Payment</th>
                                <th className="py-3 px-4">Purchased</th>
                                <th className="py-3 px-4 text-right">Carat</th>
                                <th className="py-3 px-4 text-right">Stones</th>
                                <th className="py-3 px-4 text-right">Size</th>
                                <th className="py-3 px-4">MM</th>
                                <th className="py-3 px-4 text-right">$/ct</th>
                                <th className="py-3 px-4 text-right">Total</th>
                                <th className="py-3 px-4 text-right">Children · Jewelry</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedParcels.map(p => (
                                <tr key={p.id} className="border-b border-stone-200/70 hover:bg-stone-100" data-testid={`parcel-row-${p.id}`}>
                                    <td className="py-3 px-4">
                                        <div className="flex items-center gap-2" style={{ paddingLeft: `${p._depth * 20}px` }}>
                                            {p._depth > 0 && (
                                                <span className="text-stone-400 font-mono text-xs select-none" aria-hidden="true">└─</span>
                                            )}
                                            <Link to={`/parcels/${p.id}`} className="font-mono text-stone-900 hover:text-emerald-700 transition-colors" data-testid={`parcel-code-${p.id}`}>
                                                {p.code}
                                            </Link>
                                            {!p.parent_id && (
                                                <span className="text-[9px] uppercase tracking-widest text-amber-700 border border-amber-500/40 bg-amber-50 px-1.5 py-0.5" data-testid={`root-badge-${p.id}`} title="Root parcel">
                                                    ★ Root
                                                </span>
                                            )}
                                        </div>
                                        {p.name && <div className="text-[11px] text-stone-500" style={{ paddingLeft: `${p._depth * 20 + (p._depth > 0 ? 20 : 0)}px` }}>{p.name}</div>}
                                    </td>
                                    <td className="py-3 px-4"><StatusBadge status={p.status} kind="parcel" linkTo={(p.status === "in_production" || p.status === "set_in_jewelry") && p.current_jewelry_id ? `/jewelry/${p.current_jewelry_id}` : null} /></td>
                                    <td className="py-3 px-4">
                                        <PaymentBadge summary={p.payments_summary} isRoot={!p.parent_id} testid={`parcel-payment-badge-${p.id}`} />
                                    </td>
                                    <td className="py-3 px-4 text-stone-700">{fmtDate(p.purchase_date)}</td>
                                    <td className="py-3 px-4 text-right num" data-testid={`parcel-carat-${p.id}`}>
                                        {fmtCarat(p.total_carat)}
                                        {p.root?.original_carat != null && (
                                            <div className="text-[10px] uppercase tracking-widest text-amber-700 mt-0.5" data-testid={`parcel-root-carat-${p.id}`} title={`Split from root ${p.root.code}`}>
                                                of {fmtCarat(p.root.original_carat)} ct
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-right num text-stone-700">{p.number_of_stones ?? "—"}</td>
                                    <td className="py-3 px-4 text-right num text-stone-700">{fmtSize(p.size)}</td>
                                    <td className="py-3 px-4 text-stone-700 text-xs">{p.mm || "—"}</td>
                                    <td className="py-3 px-4 text-right num text-stone-700">{fmtUSD(p.price_per_carat)}</td>
                                    <td className="py-3 px-4 text-right num text-stone-900">{fmtUSD(p.total_cost)}</td>
                                    <td className="py-3 px-4 text-right text-stone-500 text-xs">{p.children_count} · {p.jewelry_count}</td>
                                    <td className="py-3 px-4 text-right">
                                        {canWrite && (
                                            <button onClick={() => remove(p.id)} className="text-stone-400 hover:text-rose-600" data-testid={`delete-parcel-${p.id}`}>
                                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <TableSummaryRow
                            rows={sortedParcels}
                            testid="parcels-summary-row"
                            cells={[
                                { type: "label", text: `${sortedParcels.length} rows` },
                                null, null, null,
                                { type: "sum-carat", get: r => r.total_carat, testid: "parcels-summary-carat" },
                                { type: "sum", get: r => r.number_of_stones, testid: "parcels-summary-stones" },
                                null, null,
                                { type: "avg-usd", get: r => r.price_per_carat, testid: "parcels-summary-ppc" },
                                { type: "sum-usd", get: r => r.total_cost, testid: "parcels-summary-total" },
                                null, null,
                            ]}
                        />
                    </table>
                </div>
            )}
        </div>
    );
}

const Field = ({ label, children }) => (
    <div><Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 block">{label}</Label>{children}</div>
);

// -------- Filter chip primitives --------
const FilterChip = ({ label, count, testid, children }) => (
    <Popover>
        <PopoverTrigger asChild>
            <button
                type="button"
                className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[11px] uppercase tracking-widest transition-colors ${count > 0 ? "border-emerald-500/60 bg-emerald-50 text-emerald-800" : "border-stone-300 bg-white text-stone-600 hover:border-stone-400"}`}
                data-testid={testid}
            >
                {label}
                {count > 0 && <span className="num text-[10px] text-emerald-700">({count})</span>}
            </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="bg-[#faf7ef] border-stone-200 rounded-none p-3 w-auto">
            {children}
        </PopoverContent>
    </Popover>
);

const ChipList = ({ scrollable, children }) => (
    <div className={`flex flex-col gap-1 ${scrollable ? "max-h-52 overflow-y-auto pr-1" : ""}`}>{children}</div>
);

const ChipOption = ({ checked, onChange, testid, children }) => (
    <label className="flex items-center gap-2 text-xs text-stone-700 hover:bg-stone-100 px-1 py-1 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={onChange} className="accent-emerald-600" data-testid={testid} />
        <span>{children}</span>
    </label>
);

const PaymentBadge = ({ summary, isRoot, testid }) => {
    if (!summary) return <span className="text-stone-400 text-xs">—</span>;
    const map = {
        paid: { label: "Paid", cls: "border-emerald-500/40 bg-emerald-50 text-emerald-700" },
        partial: { label: "Partial", cls: "border-amber-500/40 bg-amber-50 text-amber-700" },
        unpaid: { label: "Unpaid", cls: "border-rose-500/40 bg-rose-50 text-rose-700" },
    };
    const m = map[summary.status] || map.unpaid;
    const own = summary.supplier_outstanding || 0;
    const descs = summary.descendants_outstanding || 0;
    const totalWithSubs = summary.total_outstanding_with_descendants || own;
    const showRollUp = isRoot && descs > 0.01;
    return (
        <div className="flex flex-col items-start gap-0.5">
            <span className={`inline-flex items-center gap-1.5 border px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${m.cls}`} data-testid={testid} title={`Paid ${fmtUSD(summary.total_paid)} of ${fmtUSD(summary.supplier_liability)}`}>
                {m.label}
                {summary.status !== "paid" && (
                    <span className="num font-normal opacity-75">{fmtUSD(own)} left</span>
                )}
            </span>
            {showRollUp && (
                <span className="text-[10px] text-stone-500 num" data-testid={`${testid}-total`} title="Own outstanding + all unpaid sub-parcels">
                    total incl. subs: <span className="text-rose-600">{fmtUSD(totalWithSubs)}</span>
                </span>
            )}
        </div>
    );
};
